// A2F-547 / D70: profile assembly contract (inherit-don't-copy).
//
// The signup POST body and the /configure save body both build a Profile
// without a `formatters` key. Ribbon handlers (A2F-533) fall back to
// CHRONA_DEFAULT for missing formatter groups, so absent formatters
// means "use Flavia's current defaults." When Flavia improves
// CHRONA_DEFAULT (e.g. a new table palette), every uncustomized user
// picks it up on the next boot without a profile migration.
//
// `formatters` populates only when the user *explicitly* changes
// something in /configure and that change is written back via
// saveProfile. Signup's job is to produce a profile that inherits the
// defaults — writing CHRONA_DEFAULT into every new user's profile
// would freeze the defaults at signup time and defeat the pattern.
//
// This module is the single point where the onboarding page and the
// configurator agree on how to shape their POST body, so a future
// refactor that touches Profile shape can catch both callsites.
//
// A2F-796 — interaction with backend partial-merge: `upsert_profile`
// now treats absent top-level keys as "preserve existing JSONB value".
// Three states per field:
//   * absent from body   → preserve
//   * present + non-null → replace
//   * present + null     → clear
// `buildProfile` below currently OMITS formatters when the user's
// state matches BASE_FORMATTING (the inherit-don't-copy intent), which
// previously CLEARED the field under the old wholesale-replace
// semantics. Post-A2F-796 it PRESERVES the previously-saved formatters.
// This regresses a tail flow — a user who manually undoes every
// /configure customization expecting save to revert to CHRONA_DEFAULT
// — see backend/services/style_profile_service.py upsert_profile
// docstring for full notes. Adding a future "Reset to defaults"
// button requires sending `formatters: null` explicitly here (widen
// the Profile['formatters'] type to allow null and emit it as
// profile.formatters = null below).

import type { Formatters, Pack, Profile, Rule } from '../types'

export interface ProfileAssemblyInput {
  domain: string
  tags: Iterable<string>
  packs: Iterable<string>
  rules: Rule[]
  // A2F-650: optional formatter overrides. Only the FRE redesign
  // (OnboardPage) passes this — every other caller (configurator save,
  // legacy onboarding paths) omits it and inherits CHRONA_DEFAULT per
  // D70. Empty/undefined groups MUST be stripped before getting here so
  // we don't write `{}` into the profile and accidentally lock in a
  // "user has zero formatting" state on the backend.
  formatters?: Formatters
  // A2F-796: My-Chrona-only state — packs the user has explicitly
  // toggled off. The configurator doesn't edit this field; it must be
  // threaded through from hydration to save so a configurator save
  // doesn't wipe the user's off-state set on the other surface (the
  // backend replaces the JSONB blob wholesale on PUT).
  disabledPacks?: Iterable<string>
}

/**
 * Build a Profile for signup or save. `formatters` is optional and
 * omitted from the output unless the caller explicitly passes a
 * non-empty value — see module header for the inherit-don't-copy
 * rationale. `rules` is passed through as the already-merged /
 * deduplicated list the caller owns; this helper does not dedupe.
 */
export function buildProfile({
  domain,
  tags,
  packs,
  rules,
  formatters,
  disabledPacks,
}: ProfileAssemblyInput): Profile {
  const profile: Profile = {
    domain,
    tags: Array.from(tags),
    packs: Array.from(packs),
    rules,
  }
  if (formatters && Object.keys(formatters).length > 0) {
    profile.formatters = formatters
  }
  // A2F-796: emit disabledPacks whenever the caller passes the field
  // (even an empty array — that signals "clear" under the partial-
  // merge contract). Callers that don't intend to write the field
  // pass undefined and the key is omitted from the body, so the
  // server preserves whatever JSONB currently has. This pairs with
  // ConfiguratorPage.handleSave only including the field when the
  // user's action re-enabled a previously-disabled pack — see that
  // call site for the cross-tab-race trade-off.
  if (disabledPacks !== undefined) {
    profile.disabledPacks = Array.from(disabledPacks)
  }
  return profile
}

/**
 * Dedup-merge tag-derived rules with pack-derived rules, preserving
 * order and keeping the first occurrence of each rule id. Used by the
 * onboarding page where tags + a pack + custom rule chips fold into the
 * profile's `rules` list before signup.
 */
export function mergeRulesById(...groups: Rule[][]): Rule[] {
  const seen = new Set<string>()
  const out: Rule[] = []
  for (const group of groups) {
    for (const rule of group) {
      if (seen.has(rule.id)) continue
      seen.add(rule.id)
      out.push(rule)
    }
  }
  return out
}

/** Resolve a pack's `ruleIds` into concrete Rule objects via ALL_RULES_BY_ID. */
export function rulesForPack(
  pack: Pack,
  allRulesById: Record<string, Rule>,
): Rule[] {
  const out: Rule[] = []
  for (const rid of pack.ruleIds) {
    const rule = allRulesById[rid]
    if (rule) out.push(rule)
  }
  return out
}
